import React from "react";
import { Dialog, DialogActions } from "@material-ui/core";
import { RawLinkButton } from "../../../LinkButton";
import { IntegrationStatuses } from "../../IntegrationStatuses";
import IIntegration from "../../../../models/IIntegration";
import { CustomAPIID, saveStatus, submitForProductionServiceCall } from "../../functions/sharedFunctions";
import { FormErrors } from "../../../forms/FormErrors";
import { SubmitForProductionDevDialogContent } from "./SubmitForProductionDevDialogContent";
import { SubmitForProductionUpdateDialogContent } from "./SubmitForProductionUpdateDialogContent";
import { useIntegrationContext } from "../../contexts/IntegrationContext";

export interface IAPIFieldsDialogProps {
  integration: IIntegration;
  token: string;
  username: string;
  setForm: (val: IIntegration) => void;
  setFormErrors: (val: FormErrors<IIntegration>) => void;
}

const SubmitForProductionDialog = (props: IAPIFieldsDialogProps): JSX.Element => {
  const { integration, token, username, setForm, setFormErrors } = props;
  const { apis, access, showSubmitForProductionDialog, setShowSubmitForProductionDialog } = useIntegrationContext();

  const cancelDialog = (): void => {
    if (!showLoading) {
      setShowSubmitForProductionDialog(false);
    }
  };

  const [preValidateError, setPreValidateError] = React.useState<string | null>();
  const [submitError, setSubmitError] = React.useState<string | null>();
  const [successMessage, setSuccessMessage] = React.useState<string | null>();
  const [showSubmitButton, setShowSubmitButton] = React.useState<boolean>(true);
  const [showLoading, setShowLoading] = React.useState<boolean>(false);

  const onSubmit = async (): Promise<void> => {
    try {
      setShowSubmitButton(false);
      setShowLoading(true);

      let returnedServiceCall;
      try {
        returnedServiceCall = await submitForProductionServiceCall(integration, token, username, `Submission for Production: ${integration.Name}`);
        if (!returnedServiceCall || !returnedServiceCall.Number) {
          setSubmitError(`An unknown error occurred while creating your request. Please submit a support ticket to us.`);
          return;
        }
      } catch (err) {
        setSubmitError(`An error occurred while creating your request. Please submit a support ticket to us. Error: ${err}`);
        return;
      }

      try {
        if (integration.Status.ID === IntegrationStatuses.InDevelopment) {
          await saveStatus({
            token,
            form: integration,
            status: { ID: IntegrationStatuses.InReview, Name: "In Review" },
            setForm,
            setFormErrors,
          });
        } else {
          await saveStatus({ token, form: integration, status: integration.Status, setForm, setFormErrors });
        }
      } catch (err) {
        setSubmitError(
          `Service call ${returnedServiceCall.Number} was successfully submitted, but an error occurred while ` +
            `updating your integration. Please submit a service call to report this. Error: ${err}`,
        );
        return;
      }

      setSuccessMessage(`Service Call ${returnedServiceCall.Number} Submitted Successfully.`);
    } finally {
      setShowLoading(false);
    }
  };

  const validate = (): void => {
    if (submitError) {
      setShowSubmitButton(false);
      return;
    }

    if (successMessage) {
      setShowSubmitButton(false);
      return;
    }

    if (integration.Status.ID !== IntegrationStatuses.InDevelopment && integration.Status.ID !== IntegrationStatuses.AvailableForCustomers) {
      setShowSubmitButton(false);
      return;
    }

    if (!apis || apis.length === 0) {
      setShowSubmitButton(false);
      setPreValidateError("Integrations must have at least one API selected before being submitted for production.");
      return;
    }

    if (apis.some(api => api.ID === CustomAPIID) && !(access !== undefined && !access.ErrorLoadingProposedAccess && access.Objects.length > 0)) {
      setShowSubmitButton(false);
      setPreValidateError("Integrations that use the Custom API must upload Custom API Fields before they may be submitted for Production.");
      return;
    }

    setShowSubmitButton(true);
    setPreValidateError(null);
  };

  React.useEffect(() => {
    validate();
  }, [apis, access, integration.Status.Name, submitError, successMessage]);

  return (
    <Dialog open={showSubmitForProductionDialog} onClose={cancelDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      {(integration.Status.ID === IntegrationStatuses.InDevelopment || integration.Status.ID === IntegrationStatuses.InReview) && (
        <SubmitForProductionDevDialogContent
          showLoading={showLoading}
          preValidateError={preValidateError}
          submitError={submitError}
          successMessage={successMessage}
          name={integration.Name}
        />
      )}

      {integration.Status.ID === IntegrationStatuses.AvailableForCustomers && (
        <SubmitForProductionUpdateDialogContent
          showLoading={showLoading}
          preValidateError={preValidateError}
          submitError={submitError}
          successMessage={successMessage}
          name={integration.Name}
        />
      )}

      <DialogActions>
        {showSubmitButton && (
          <RawLinkButton onClick={onSubmit} color="secondary">
            Submit
          </RawLinkButton>
        )}
        <RawLinkButton onClick={cancelDialog} color="primary">
          Close
        </RawLinkButton>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitForProductionDialog;
